import { useRouterState } from "@tanstack/react-router";
import { createIsomorphicFn } from "@tanstack/react-start";
import { getCookie } from "@tanstack/react-start/server";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

export const LOCALES = ["ar", "fr", "en"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "ar";
export const LOCALE_COOKIE = "locale";

export const LOCALE_META: Record<
	Locale,
	{ label: string; flag: string; dir: "rtl" | "ltr"; htmlLang: string }
> = {
	ar: { label: "العربية", flag: "🇩🇿", dir: "rtl", htmlLang: "ar" },
	fr: { label: "Français", flag: "🇫🇷", dir: "ltr", htmlLang: "fr" },
	en: { label: "English", flag: "🇬🇧", dir: "ltr", htmlLang: "en" },
};

function isLocale(value: unknown): value is Locale {
	return (
		typeof value === "string" && (LOCALES as readonly string[]).includes(value)
	);
}

/* -------------------------------------------------------------------------- */
/*  Content dictionaries — Arabic is the source of truth for the shape.       */
/* -------------------------------------------------------------------------- */

const ar = {
	meta: {
		title: "دورة إنشاء المؤسسات في الجزائر | أكاديمية مستشير",
		description:
			"دورة تطبيقية مكثّفة (18 ساعة على 3 أيام) تحوّل فكرة مشروعك إلى مؤسسة تنافسية ومستدامة في السياق الاقتصادي الجزائري. سجّل الآن ليتصل بك فريقنا.",
	},
	nav: {
		trainer: "المدرّب",
		outcomes: "المخرجات",
		faq: "الأسئلة الشائعة",
		book: "احجز مقعدك",
		details: "تفاصيل الدورة",
	},
	hero: {
		eyebrow: "برنامج عملي · 18 ساعة على 3 أيام",
		titleLead: "حوّل فكرتك إلى",
		titleHighlight: "مؤسسة ناجحة ومُموّلة",
		// Shown right under the title highlight when set — e.g. the cohort's
		// dates ("5-7 سبتمبر 2026"). Empty string hides the line entirely, so a
		// training with a rolling/undated enrollment isn't forced to show one.
		date: "",
		subtitle:
			"ثلاثة أيام تطبيقية بمرافقة خبير دولي درّب أكثر من 10,000 شخص في أزيد من 10 دول، تنتقل خلالها من فكرة على الورق إلى مؤسسة مُهيكَلة قانونياً وجاهزة للتمويل، مصمّمة خصيصاً للسياق الجزائري.",
		bullets: [
			"مخطّط أعمال يُقنع البنك والمستثمر",
			"الوضعية القانونية والجبائية الصحيحة",
			"استراتيجية تجلب لك أوّل زبائنك",
		],
		trust: "انضمّ إلى أكثر من 10,000 متدرّب حول العالم",
		ctaPrimary: "احجز مقعدك الآن",
		ctaSecondary: "شاهد البرنامج",
		portraitAlt: "د. أيوب بوخاتم أثناء تنشيط دورة تدريبية",
		workshopAlt: "ورشة تطبيقية مع المشاركين",
		hoursValue: "18",
		hoursLabel: "ساعة تدريبية\nعلى 3 أيام",
		badgeExperience: "خبرة دولية في +10 دول",
	},
	stats: [
		{ value: "+10,000", label: "متدرّب حول العالم" },
		{ value: "+10", label: "دول حول العالم" },
		{ value: "9", label: "وحدات تكوينية" },
		{ value: "18", label: "ساعة تدريبية" },
		{ value: "3", label: "أيام مكثّفة" },
	],
	pains: {
		eyebrow: "لماذا هذه الدورة؟",
		heading: "معظم المشاريع تتوقّف قبل أن تبدأ",
		sub: "ليس لأن الفكرة سيّئة، بل بسبب هذه العقبات الأربع:",
		items: [
			{
				title: "لا تعرف من أين تبدأ",
				text: "فكرة رائعة بلا خطة واضحة ولا خطوات عملية ملموسة.",
			},
			{
				title: "تمويل مرفوض",
				text: "مخطّط أعمال ضعيف لا يُقنع البنك ولا المستثمر.",
			},
			{
				title: "أخطاء قانونية مكلفة",
				text: "وضعية قانونية أو جبائية خاطئة تكلّفك غالياً لاحقاً.",
			},
			{
				title: "بلا زبائن",
				text: "منتج جاهز لكن دون استراتيجية تسويق ولا زبائن أوائل.",
			},
		],
		solution: "هذه الدورة تعالج كل ذلك عملياً، على مشروعك أنت.",
	},
	pillars: {
		eyebrow: "الدورة",
		heading: "ثلاث ركائز تخرج بها جاهزاً",
		sub: "كل ركيزة مبنية على ورشات عملية تشتغل فيها على مشروعك أنت.",
		items: [
			{
				title: "مخطّط أعمال قابل للتمويل",
				text: "نموذج اقتصادي ومخطّط أعمال يُقنع البنك والمستثمر.",
			},
			{
				title: "هيكلة قانونية سليمة",
				text: "الوضعية القانونية والجبائية المناسبة، دون أخطاء مكلفة.",
			},
			{
				title: "زبائن من اليوم الأول",
				text: "استراتيجية تسويق وتنقيب تجلب لك زبائنك الأوائل.",
			},
		],
		downloadLabel: "حمّل البطاقة التقنية للدورة:",
	},
	// Client logos shown as a marquee under the course section. Empty here —
	// this training has no reference wall yet; the strip only renders when a
	// training supplies more than one logo.
	partners: {
		eyebrow: "مراجعنا",
		heading: "مؤسسات وثقت بنا",
		items: [] as { name: string; logo: string }[],
	},
	trainer: {
		eyebrow: "المدرّب",
		heading: "تتعلّم ممّن مارس الميدان",
		sub: "خبرة ميدانية في التعليم وإدارة الأعمال، درّب أكثر من 10,000 شخص في أزيد من 10 دول.",
		name: "د. أيوب بوخاتم",
		role: "خبير واستشاري دولي في الإدارة والتدريب",
		avatarAlt: "د. أيوب بوخاتم",
		// Per-trainer, not a house figure — the templates render whatever the
		// training's own dictionary says here.
		statValue: "+10,000",
		statLabel: "متدرّب",
		bio: "الدكتور أيوب بوخاتم مدرّب ومستشار في مجال التعليم وإدارة الأعمال، درّب أكثر من 10,000 شخص في أزيد من 10 دول. قدّم تدريبات واستشارات لعدد من المنظمات والمؤسسات المرموقة مثل British Council وWorld Learning وMedafco Development وSIM والكشافة الإسلامية الجزائرية والهلال الأحمر الجزائري وغيرها. بخبرته الواسعة في التعليم وإدارة الأعمال، يرشدك الدكتور بوخاتم لبناء مسارك نحو التطور والريادة.",
		why: [
			{
				q: "مدرّب بخبرة دولية",
				a: "د. أيوب بوخاتم، خبير واستشاري دولي في الإدارة والتدريب، درّب أكثر من 10,000 شخص في أزيد من 10 دول. مجالات خبرته: التعليم والتكوين، الاستشارات، المشاريع، والإدارة.",
			},
			{
				q: "التعلّم بالممارسة",
				a: "تشتغل مباشرة على مشروعك الشخصي في الورشات، لا محاضرات نظرية جامدة.",
			},
			{
				q: "مصمّمة للسياق الجزائري",
				a: "الإجراءات والأشكال القانونية والالتزامات الجبائية كما هي فعلياً في الجزائر.",
			},
			{
				q: "شهادة ومتابعة",
				a: "شهادة نجاح معتمدة والانضمام إلى شبكة الخريجين بعد الدورة.",
			},
		],
	},
	outcomes: {
		audienceHeading: "لمن هذه الدورة؟",
		audience: [
			"حاملو المشاريع",
			"منشئو المؤسسات",
			"رواد الأعمال",
			"مسيّرو المؤسسات الصغيرة والمتوسطة",
			"مسؤولو التطوير",
		],
		objectivesHeading: "النتائج المتوقعة",
		objectives: [
			"تطوير السلوك الريادي وتحديد فرصة موثّقة",
			"بناء نموذج أعمال ومخطّط قابل للتمويل",
			"اختيار الشكل القانوني وإتقان إجراءات التأسيس",
			"استراتيجية تسويقية وقيادة نمو مستدام",
		],
		practiceValue: "9",
		practiceNote:
			"وحدات تكوينية على 18 ساعة، تُختتم بورشة تطبيقية جماعية على مشروع كل مشارك.",
	},
	faq: {
		eyebrow: "الأسئلة الشائعة",
		heading: "سؤالك على الأرجح هنا",
		sub: "لم تجد إجابتك؟ اترك رقمك وسنتّصل بك.",
		items: [
			{
				q: "هل أحتاج خبرة مسبقة؟",
				a: "لا يُشترط أي متطلب خاص. امتلاك فكرة مشروع أو نشاط قائم ميزة إضافية فقط.",
			},
			{
				q: "كم تدوم الدورة؟",
				a: "18 ساعة تدريبية موزّعة على 3 أيام مكثّفة.",
			},
			{
				q: "هل أتحصّل على شهادة؟",
				a: "نعم، شهادة نجاح مع الانضمام إلى شبكة خريجي أكاديمية مستشير.",
			},
			{
				q: "كيف أعرف الأسعار والمواعيد؟",
				a: "اترك بياناتك في الأسفل وسيتواصل معك فريقنا ليشرح التفاصيل الكاملة والمواعيد المتاحة والأسعار.",
			},
		],
	},
	register: {
		eyebrow: "التسجيل",
		heading: "احجز مقعدك الآن",
		sub: "يتّصل بك فريقنا ليشرح لك البرنامج الكامل والمواعيد والأسعار، دون أي التزام.",
		imageAlt: "د. أيوب بوخاتم أثناء ورشة تدريبية",
	},
	footer: {
		tagline: "أكاديمية مستشير لريادة الأعمال وتطوير المؤسسات.",
		book: "احجز مقعدك",
		rights: "© 2026 أكاديمية مستشير. جميع الحقوق محفوظة.",
	},
	whatsapp: {
		aria: "تواصل عبر واتساب",
		title: "واتساب",
		prefill: "السلام عليكم، أريد الاستفسار عن دورة إنشاء المؤسسات وحجز مقعد.",
	},
	messenger: {
		aria: "تواصل معنا عبر ماسنجر",
		title: "ماسنجر",
	},
	form: {
		fullName: "الاسم الكامل",
		phone: "رقم الهاتف",
		email: "البريد الإلكتروني",
		wilaya: "الولاية",
		wilayaPlaceholder: "اختر الولاية",
		jobStatus: "الوضعية الحالية",
		jobStatusPlaceholder: "اختر وضعيتك",
		submit: "احجز مقعدك الآن",
		privacy: "بياناتك لا تُشارك مع أي جهة.",
		successTitle: "تم استلام طلبك",
		successText: "سيتواصل معك فريقنا قريباً لإتمام التسجيل والإجابة عن أسئلتك.",
		errName: "يرجى إدخال الاسم الكامل",
		errPhone: "يرجى إدخال رقم هاتف جزائري صالح",
		errEmail: "يرجى إدخال بريد إلكتروني صالح",
		errWilaya: "يرجى اختيار الولاية",
		errJob: "يرجى اختيار وضعيتك الحالية",
		errGeneric: "حدث خطأ ما، حاول مجدداً أو تواصل معنا مباشرة.",
		jobStatuses: [
			"موظف",
			"طالب",
			"صاحب مشروع",
			"مهني حر أو حرفي",
			"باحث عن عمل",
			"أخرى",
		],
	},
	langSwitch: { label: "اللغة" },
	brochure: {
		metaTitle:
			"البطاقة التقنية الكاملة | دورة إنشاء المؤسسات | أكاديمية مستشير",
		metaDescription:
			"البرنامج التفصيلي الكامل لدورة إنشاء المؤسسات في الجزائر.",
		cardCode: "بطاقة تدريب MA-ENT-01",
		eyebrow: "ريادة الأعمال",
		title: "إنشاء المؤسسات في الجزائر",
		intro:
			"تحويل فكرة أو مشروع إلى مؤسسة منظّمة وتنافسية وقادرة على التطور المستدام في السياق الاقتصادي الجزائري.",
		formatLabel: "الصيغة",
		formatValue: "ورشات تطبيقية جماعية",
		durationLabel: "المدة",
		durationValue: "18 ساعة تدريبية (3 أيام)",
		audienceLabel: "الفئة المستهدفة",
		audienceValue:
			"حاملو المشاريع، منشئو المؤسسات، رواد الأعمال، مسيّرو المؤسسات الصغيرة والمتوسطة، ومسؤولو التطوير.",
		prereqHeading: "المتطلبات المسبقة",
		prereqText:
			"لا يُشترط أي متطلب خاص. تشكّل فكرة المشروع أو النشاط القائم ميزة إضافية.",
		objectivesHeading: "النتائج المتوقعة",
		objectives: [
			"تطوير السلوك الريادي وتحديد فرصة عمل موثّقة.",
			"بناء نموذج أعمال ناجح وإعداد مخطّط أعمال شامل وقابل للتمويل البنكي.",
			"اختيار الشكل القانوني المناسب وإتقان الإجراءات الإدارية لتأسيس المؤسسة في الجزائر.",
			"تحديد استراتيجية تسويقية وتجارية وقيادة تطوّر المؤسسة بشكل مستدام.",
		],
		programHeading: "البرنامج التفصيلي",
		programSplit: "9 وحدات تكوينية · 18 ساعة على 3 أيام",
		modules: [
			{
				title: "الوحدة 1: تنمية الروح الريادية",
				text: "خصائص رائد الأعمال الناجح، عوامل النجاح الأساسية، الأخطاء الواجب تجنّبها، الرؤية الريادية وسلوك القائد.",
			},
			{
				title: "الوحدة 2: تحديد الفرصة وتحليل السوق",
				text: "تحديد فرصة العمل، دراسة السوق، تحليل المنافسة، الزبائن المستهدفون، والعرض ذو القيمة.",
			},
			{
				title: "الوحدة 3: تصميم النموذج الاقتصادي",
				text: "مخطّط نموذج الأعمال (Business Model Canvas)، خلق القيمة، مصادر الإيرادات، هيكل التكاليف، والتحقق من صحة النموذج الاقتصادي.",
			},
			{
				title: "الوحدة 4: إعداد مخطّط الأعمال",
				text: "تقديم المشروع، الدراسة التجارية، الخطة التسويقية، التنظيم، التوقعات المالية، وخطة التمويل.",
			},
			{
				title: "الوحدة 5: الهيكلة القانونية والإدارية والجبائية",
				text: "الأشكال القانونية في الجزائر، اختيار الوضعية المناسبة، إجراءات التأسيس الإدارية، الالتزامات الجبائية والاجتماعية، والتنظيم الإداري.",
			},
			{
				title: "الوحدة 6: التسويق والتطوير التجاري",
				text: "التموقع والسياسة التجارية، التنقيب واكتساب الزبائن، الولاء، التسويق الرقمي، وتطوير محفظة الزبائن.",
			},
			{
				title: "الوحدة 7: تنظيم المؤسسة وقيادتها",
				text: "التنظيم الداخلي، لوحات القيادة ومؤشرات الأداء (KPI)، إدارة المخاطر، والتحسين المستمر.",
			},
			{
				title: "الوحدة 8: قيادة المسيّر والتنمية المستدامة",
				text: "القيادة الريادية، التواصل، التحفيز، إدارة التغيير، استدامة المؤسسة، والرؤية الاستراتيجية.",
			},
			{
				title: "الوحدة 9: التطوير والنمو والاستدامة",
				text: "استراتيجيات النمو التدريجي، تطوير رقم الأعمال، تنويع العروض، الشراكات الاستراتيجية، الابتكار، وإدارة مخاطر النمو.",
			},
		],
		workshopTitle: "ورشة تطبيقية",
		workshopText:
			"إعداد جماعي لمخطّط نموذج الأعمال والشروع في وضع مخطّط أعمال مبسّط انطلاقاً من مشروع كل مشارك.",
		approachHeading: "المقاربة التربوية",
		approachText:
			"تعتمد مقاربتنا على التطبيق العملي (70%) أكثر من الجانب النظري (30%). يعمل المشاركون على حالات ملموسة وعلى مشروعهم الشخصي (التعلّم بالممارسة) لضمان قابلية تطبيق فورية.",
		evaluationHeading: "التقييم والمتابعة",
		evaluation: [
			{ step: "ما قبل الدورة", text: "استبيان تموضعي لتقييم المستوى." },
			{
				step: "أثناء الدورة",
				text: "اختبارات تفاعلية والتحقّق من التقدّم على حالات من مشروع المشارك الشخصي من طرف المدرّب.",
			},
			{
				step: "ما بعد الدورة",
				text: "شهادة نجاح والانضمام إلى شبكة الخريجين.",
			},
		],
		trainerHeading: "المدرّب",
		trainerCreds: [
			{ value: "+10,000", label: "متدرّب حول العالم" },
			{ value: "+10", label: "دول قدّم فيها تدريباته" },
		],
		includedHeading: "ما يشمله التكوين",
		included: [
			"شهادة نجاح",
			"الانضمام إلى شبكة الخريجين",
			"قوالب ووثائق عملية (مخطّط الأعمال وBMC)",
			"ورشات تطبيقية على مشروعك الشخصي",
			"استبيان تموضعي قبل التكوين",
		],
		downloadHeading: "حمّل البطاقة الكاملة (PDF)",
		downloadNote: "النسخة الكاملة القابلة للطباعة، بالعربية والفرنسية.",
		downloadAr: "عربية (AR)",
		downloadFr: "Français (FR)",
		ctaHeading: "جاهز لبناء مؤسستك؟",
		ctaText:
			"اترك بياناتك وسيتواصل معك فريقنا بالمواعيد والأسعار وكل التفاصيل، دون أي التزام.",
		ctaButton: "احجز مقعدك الآن",
		backToSite: "العودة إلى الصفحة الرئيسية",
	},
};

export type Dict = typeof ar;

const fr: Dict = {
	meta: {
		title: "Formation Création d'entreprise en Algérie | Académie Moustachir",
		description:
			"Formation intensive et pratique (18 h sur 3 jours) qui transforme votre idée en une entreprise compétitive et durable, dans le contexte économique algérien. Inscrivez-vous, notre équipe vous rappelle.",
	},
	nav: {
		trainer: "Le formateur",
		outcomes: "Objectifs",
		faq: "FAQ",
		book: "Réserver ma place",
		details: "Détails de la formation",
	},
	hero: {
		eyebrow: "Programme pratique · 18 h sur 3 jours",
		titleLead: "Transformez votre idée en",
		titleHighlight: "entreprise rentable et financée",
		date: "",
		subtitle:
			"Trois jours intensifs accompagnés par un expert international ayant formé plus de 10 000 personnes dans plus de 10 pays, pour passer d'une idée sur le papier à une entreprise structurée juridiquement et prête à être financée. Le tout pensé pour le contexte algérien.",
		bullets: [
			"Un business plan qui convainc la banque et l'investisseur",
			"Le bon statut juridique et fiscal",
			"Une stratégie qui vous amène vos premiers clients",
		],
		trust: "Rejoignez plus de 10 000 apprenants dans le monde",
		ctaPrimary: "Réserver ma place",
		ctaSecondary: "Voir le programme",
		portraitAlt: "Dr Ayoub Boukhatem animant une formation",
		workshopAlt: "Atelier pratique avec les participants",
		hoursValue: "18",
		hoursLabel: "heures de formation\nsur 3 jours",
		badgeExperience: "Expérience dans +10 pays",
	},
	stats: [
		{ value: "+10 000", label: "apprenants dans le monde" },
		{ value: "+10", label: "pays dans le monde" },
		{ value: "9", label: "modules de formation" },
		{ value: "18", label: "heures de formation" },
		{ value: "3", label: "jours intensifs" },
	],
	pains: {
		eyebrow: "Pourquoi cette formation ?",
		heading: "La plupart des projets s'arrêtent avant de commencer",
		sub: "Non pas à cause d'une mauvaise idée, mais à cause de ces quatre obstacles :",
		items: [
			{
				title: "Vous ne savez pas par où commencer",
				text: "Une excellente idée, sans plan clair ni étapes concrètes.",
			},
			{
				title: "Financement refusé",
				text: "Un business plan faible qui ne convainc ni la banque ni l'investisseur.",
			},
			{
				title: "Erreurs juridiques coûteuses",
				text: "Un statut juridique ou fiscal inadapté qui coûte cher plus tard.",
			},
			{
				title: "Aucun client",
				text: "Un produit prêt, mais sans stratégie marketing ni premiers clients.",
			},
		],
		solution:
			"Cette formation règle tout cela, concrètement, sur votre propre projet.",
	},
	pillars: {
		eyebrow: "La formation",
		heading: "Trois piliers pour être prêt",
		sub: "Chaque pilier repose sur des ateliers pratiques où vous travaillez sur votre propre projet.",
		items: [
			{
				title: "Un business plan finançable",
				text: "Un modèle économique et un business plan qui convainquent la banque et l'investisseur.",
			},
			{
				title: "Une structure juridique solide",
				text: "Le statut juridique et fiscal adapté, sans erreurs coûteuses.",
			},
			{
				title: "Des clients dès le premier jour",
				text: "Une stratégie de marketing et de prospection qui vous amène vos premiers clients.",
			},
		],
		downloadLabel: "Téléchargez la fiche technique :",
	},
	partners: {
		eyebrow: "Nos références",
		heading: "Ils nous ont fait confiance",
		items: [] as { name: string; logo: string }[],
	},
	trainer: {
		eyebrow: "Le formateur",
		heading: "Apprenez de quelqu'un du terrain",
		sub: "Une expérience de terrain en éducation et en management, avec plus de 10 000 personnes formées dans plus de 10 pays.",
		name: "Dr Ayoub Boukhatem",
		role: "Expert et consultant international en management et formation",
		avatarAlt: "Dr Ayoub Boukhatem",
		statValue: "+10 000",
		statLabel: "apprenants",
		bio: "Le Dr Ayoub Boukhatem est formateur et consultant en éducation et en management. Il a formé plus de 10 000 personnes dans plus de 10 pays et est intervenu en formation et en conseil auprès d'organisations et d'institutions de renom telles que le British Council, World Learning, Medafco Development, SIM, les Scouts Musulmans Algériens et le Croissant-Rouge algérien, entre autres. Fort de sa large expérience en éducation et en management, le Dr Boukhatem vous guide dans la construction de votre parcours vers le développement et l'entrepreneuriat.",
		why: [
			{
				q: "Un formateur d'envergure internationale",
				a: "Le Dr Ayoub Boukhatem, expert et consultant international en management et formation, a formé plus de 10 000 personnes dans plus de 10 pays. Ses domaines d'expertise : éducation et formation, conseil, projets, et management.",
			},
			{
				q: "Apprendre par la pratique",
				a: "Vous travaillez directement sur votre propre projet en atelier, pas de cours théoriques figés.",
			},
			{
				q: "Conçue pour le contexte algérien",
				a: "Les démarches, les formes juridiques et les obligations fiscales telles qu'elles sont réellement en Algérie.",
			},
			{
				q: "Certificat et suivi",
				a: "Un certificat de réussite reconnu et l'intégration au réseau des lauréats après la formation.",
			},
		],
	},
	outcomes: {
		audienceHeading: "À qui s'adresse cette formation ?",
		audience: [
			"Porteurs de projets",
			"Créateurs d'entreprise",
			"Entrepreneurs",
			"Dirigeants de PME",
			"Responsables du développement",
		],
		objectivesHeading: "Résultats attendus",
		objectives: [
			"Développer le comportement entrepreneurial et identifier une opportunité documentée",
			"Construire un modèle économique et un plan finançable",
			"Choisir la forme juridique et maîtriser les démarches de création",
			"Définir une stratégie marketing et piloter une croissance durable",
		],
		practiceValue: "9",
		practiceNote:
			"du temps de la formation est consacré à la pratique sur des cas concrets et votre propre projet, les 30 % restants à l'encadrement théorique.",
	},
	faq: {
		eyebrow: "FAQ",
		heading: "Votre question est sûrement ici",
		sub: "Vous ne trouvez pas votre réponse ? Laissez votre numéro, on vous rappelle.",
		items: [
			{
				q: "Faut-il une expérience préalable ?",
				a: "Aucun prérequis particulier. Avoir une idée de projet ou une activité existante est un plus.",
			},
			{
				q: "Combien de temps dure la formation ?",
				a: "18 heures de formation réparties sur 3 jours intensifs.",
			},
			{
				q: "Vais-je obtenir un certificat ?",
				a: "Oui, un certificat de réussite reconnu et l'intégration au réseau des lauréats de l'Académie Moustachir.",
			},
			{
				q: "Comment connaître les tarifs et les dates ?",
				a: "Laissez vos coordonnées ci-dessous et notre équipe vous expliquera tous les détails, les dates disponibles et les tarifs.",
			},
		],
	},
	register: {
		eyebrow: "Inscription",
		heading: "Réservez votre place",
		sub: "Notre équipe vous rappelle pour vous présenter le programme complet, les dates et les tarifs, sans engagement.",
		imageAlt: "Dr Ayoub Boukhatem lors d'un atelier de formation",
	},
	footer: {
		tagline:
			"Académie Moustachir, entrepreneuriat et développement des entreprises.",
		book: "Réserver ma place",
		rights: "© 2026 Académie Moustachir. Tous droits réservés.",
	},
	whatsapp: {
		aria: "Contacter sur WhatsApp",
		title: "WhatsApp",
		prefill:
			"Bonjour, je souhaite en savoir plus sur la formation « Création d'entreprise » et réserver une place.",
	},
	messenger: {
		aria: "Contactez-nous sur Messenger",
		title: "Messenger",
	},
	form: {
		fullName: "Nom complet",
		phone: "Numéro de téléphone",
		email: "E-mail",
		wilaya: "Wilaya",
		wilayaPlaceholder: "Votre wilaya",
		jobStatus: "Votre situation actuelle",
		jobStatusPlaceholder: "Votre situation",
		submit: "Réserver ma place",
		privacy: "Vos données ne sont partagées avec personne.",
		successTitle: "Demande bien reçue",
		successText:
			"Notre équipe vous contactera bientôt pour finaliser l'inscription et répondre à vos questions.",
		errName: "Veuillez saisir votre nom complet",
		errPhone: "Veuillez saisir un numéro de téléphone algérien valide",
		errEmail: "Veuillez saisir une adresse e-mail valide",
		errWilaya: "Veuillez choisir votre wilaya",
		errJob: "Veuillez choisir votre situation actuelle",
		errGeneric:
			"Une erreur est survenue, réessayez ou contactez-nous directement.",
		jobStatuses: [
			"Salarié",
			"Étudiant",
			"Porteur de projet",
			"Indépendant ou artisan",
			"En recherche d'emploi",
			"Autre",
		],
	},
	langSwitch: { label: "Langue" },
	brochure: {
		metaTitle:
			"Fiche technique complète | Création d'entreprise | Académie Moustachir",
		metaDescription:
			"Le programme détaillé complet de la formation Création d'entreprise en Algérie.",
		cardCode: "Fiche de formation MA-ENT-01",
		eyebrow: "Entrepreneuriat",
		title: "Création d'entreprise en Algérie",
		intro:
			"Transformer une idée ou un projet en une entreprise organisée, compétitive et capable de se développer durablement dans le contexte économique algérien.",
		formatLabel: "Format",
		formatValue: "Ateliers pratiques en groupe",
		durationLabel: "Durée",
		durationValue: "18 heures de formation (3 jours)",
		audienceLabel: "Public cible",
		audienceValue:
			"Porteurs de projets, créateurs d'entreprise, entrepreneurs, dirigeants de PME et responsables du développement.",
		prereqHeading: "Prérequis",
		prereqText:
			"Aucun prérequis particulier. Une idée de projet ou une activité existante constitue un plus.",
		objectivesHeading: "Résultats attendus",
		objectives: [
			"Développer le comportement entrepreneurial et identifier une opportunité d'affaires documentée.",
			"Construire un modèle économique viable et élaborer un business plan complet et finançable par la banque.",
			"Choisir la forme juridique adaptée et maîtriser les démarches administratives de création en Algérie.",
			"Définir une stratégie marketing et commerciale et piloter le développement durable de l'entreprise.",
		],
		programHeading: "Programme détaillé",
		programSplit: "9 modules · 18 heures sur 3 jours",
		modules: [
			{
				title: "Module 1: Développer l'esprit entrepreneurial",
				text: "Caractéristiques de l'entrepreneur qui réussit, facteurs clés de succès, erreurs à éviter, vision entrepreneuriale et posture de leader.",
			},
			{
				title: "Module 2: Identifier l'opportunité et analyser le marché",
				text: "Identification de l'opportunité, étude de marché, analyse de la concurrence, clients cibles et proposition de valeur.",
			},
			{
				title: "Module 3: Concevoir le modèle économique",
				text: "Business Model Canvas, création de valeur, sources de revenus, structure de coûts et validation du modèle économique.",
			},
			{
				title: "Module 4: Élaborer le business plan",
				text: "Présentation du projet, étude commerciale, plan marketing, organisation, prévisions financières et plan de financement.",
			},
			{
				title: "Module 5: Structuration juridique, administrative et fiscale",
				text: "Formes juridiques en Algérie, choix du statut adapté, démarches administratives de création, obligations fiscales et sociales, organisation administrative.",
			},
			{
				title: "Module 6: Marketing et développement commercial",
				text: "Positionnement et politique commerciale, prospection et acquisition de clients, fidélisation, marketing digital et développement du portefeuille clients.",
			},
			{
				title: "Module 7: Organisation et pilotage de l'entreprise",
				text: "Organisation interne, tableaux de bord et indicateurs de performance (KPI), gestion des risques et amélioration continue.",
			},
			{
				title: "Module 8: Leadership du dirigeant et développement durable",
				text: "Leadership entrepreneurial, communication, motivation, conduite du changement, pérennité de l'entreprise et vision stratégique.",
			},
			{
				title: "Module 9: Développement, croissance et pérennité",
				text: "Stratégies de croissance progressive, développement du chiffre d'affaires, diversification des offres, partenariats stratégiques, innovation et gestion des risques de croissance.",
			},
		],
		workshopTitle: "Atelier pratique",
		workshopText:
			"Élaboration collective du Business Model Canvas et démarrage d'un business plan simplifié à partir du projet de chaque participant.",
		approachHeading: "Approche pédagogique",
		approachText:
			"Notre approche privilégie la pratique (70 %) à la théorie (30 %). Les participants travaillent sur des cas concrets et sur leur propre projet (apprentissage par la pratique) pour garantir une applicabilité immédiate.",
		evaluationHeading: "Évaluation et suivi",
		evaluation: [
			{
				step: "Avant la formation",
				text: "Questionnaire de positionnement pour évaluer le niveau.",
			},
			{
				step: "Pendant la formation",
				text: "Exercices interactifs et vérification des progrès sur des cas du projet personnel du participant, par le formateur.",
			},
			{
				step: "Après la formation",
				text: "Certificat de réussite et intégration au réseau des lauréats pour garantir une applicabilité immédiate.",
			},
		],
		trainerHeading: "Le formateur",
		trainerCreds: [
			{ value: "+10 000", label: "apprenants dans le monde" },
			{ value: "+10", label: "pays où il a formé" },
		],
		includedHeading: "Ce qui est inclus",
		included: [
			"Certificat de réussite reconnu",
			"Intégration au réseau des lauréats",
			"Modèles et documents pratiques (business plan et BMC)",
			"Ateliers pratiques sur votre propre projet",
			"Suivi après la formation",
		],
		downloadHeading: "Télécharger la fiche complète (PDF)",
		downloadNote: "La version complète imprimable, en arabe et en français.",
		downloadAr: "عربية (AR)",
		downloadFr: "Français (FR)",
		ctaHeading: "Prêt à bâtir votre entreprise ?",
		ctaText:
			"Laissez vos coordonnées et notre équipe vous recontacte avec les dates, les tarifs et tous les détails, sans engagement.",
		ctaButton: "Réserver ma place",
		backToSite: "Retour à l'accueil",
	},
};

const en: Dict = {
	meta: {
		title: "Company Creation Course in Algeria | Moustachir Academy",
		description:
			"An intensive, hands-on program (18 hours over 3 days) that turns your idea into a competitive, sustainable company in the Algerian economic context. Register and our team will call you.",
	},
	nav: {
		trainer: "The trainer",
		outcomes: "Outcomes",
		faq: "FAQ",
		book: "Book my seat",
		details: "Course details",
	},
	hero: {
		eyebrow: "Hands-on program · 18 h over 3 days",
		titleLead: "Turn your idea into a",
		titleHighlight: "profitable, funded company",
		date: "",
		subtitle:
			"Three intensive days guided by an international expert who has trained more than 10,000 people across over 10 countries, taking you from an idea on paper to a legally structured, funding-ready company. Built for the Algerian context.",
		bullets: [
			"A business plan that convinces the bank and the investor",
			"The right legal and tax status",
			"A strategy that brings you your first clients",
		],
		trust: "Join more than 10,000 trainees worldwide",
		ctaPrimary: "Book my seat",
		ctaSecondary: "See the program",
		portraitAlt: "Dr. Ayoub Boukhatem leading a training session",
		workshopAlt: "Hands-on workshop with participants",
		hoursValue: "18",
		hoursLabel: "training hours\nover 3 days",
		badgeExperience: "Experience across +10 countries",
	},
	stats: [
		{ value: "+10,000", label: "trainees worldwide" },
		{ value: "+10", label: "countries worldwide" },
		{ value: "9", label: "training modules" },
		{ value: "18", label: "training hours" },
		{ value: "3", label: "intensive days" },
	],
	pains: {
		eyebrow: "Why this course?",
		heading: "Most projects stop before they start",
		sub: "Not because the idea is bad, but because of these four obstacles:",
		items: [
			{
				title: "You don't know where to start",
				text: "A great idea, with no clear plan or concrete steps.",
			},
			{
				title: "Funding refused",
				text: "A weak business plan that convinces neither bank nor investor.",
			},
			{
				title: "Costly legal mistakes",
				text: "The wrong legal or tax status that costs you dearly later.",
			},
			{
				title: "No clients",
				text: "A ready product, but no marketing strategy and no first clients.",
			},
		],
		solution: "This course solves all of it, hands-on, on your own project.",
	},
	pillars: {
		eyebrow: "The course",
		heading: "Three pillars to leave ready",
		sub: "Each pillar is built on practical workshops where you work on your own project.",
		items: [
			{
				title: "A fundable business plan",
				text: "An economic model and business plan that convince the bank and the investor.",
			},
			{
				title: "A solid legal structure",
				text: "The right legal and tax status, without costly mistakes.",
			},
			{
				title: "Clients from day one",
				text: "A marketing and prospecting strategy that brings you your first clients.",
			},
		],
		downloadLabel: "Download the course fact sheet:",
	},
	partners: {
		eyebrow: "Our references",
		heading: "Organizations that trusted us",
		items: [] as { name: string; logo: string }[],
	},
	trainer: {
		eyebrow: "The trainer",
		heading: "Learn from someone who's done it",
		sub: "Field experience in education and business management, with more than 10,000 people trained across over 10 countries.",
		name: "Dr. Ayoub Boukhatem",
		role: "International expert and consultant in management and training",
		avatarAlt: "Dr. Ayoub Boukhatem",
		statValue: "+10,000",
		statLabel: "trainees",
		bio: "Dr. Ayoub Boukhatem is a trainer and consultant in education and business management. He has trained more than 10,000 people across over 10 countries, and has delivered training and consulting for renowned organizations and institutions including the British Council, World Learning, Medafco Development, SIM, the Algerian Muslim Scouts, and the Algerian Red Crescent, among others. Drawing on his broad experience in education and management, Dr. Boukhatem guides you in building your own path toward growth and entrepreneurship.",
		why: [
			{
				q: "A trainer with international experience",
				a: "Dr. Ayoub Boukhatem, international expert and consultant in management and training, has trained over 10,000 people in more than 10 countries.",
			},
			{
				q: "Learning by doing",
				a: "You work directly on your own project in workshops, not through rigid theoretical lectures.",
			},
			{
				q: "Built for the Algerian context",
				a: "The procedures, legal forms and tax obligations exactly as they are in Algeria.",
			},
			{
				q: "Certificate and follow-up",
				a: "A recognized certificate of completion and access to the alumni network after the course.",
			},
		],
	},
	outcomes: {
		audienceHeading: "Who is this course for?",
		audience: [
			"Project owners",
			"Business founders",
			"Entrepreneurs",
			"SME managers",
			"Development officers",
		],
		objectivesHeading: "Expected outcomes",
		objectives: [
			"Develop entrepreneurial behavior and identify a documented opportunity",
			"Build a business model and a fundable plan",
			"Choose the legal form and master the setup procedures",
			"Define a marketing strategy and drive sustainable growth",
		],
		practiceValue: "9",
		practiceNote:
			"of the course is hands-on practice on concrete cases and your own project, and the remaining 30% is theoretical framing.",
	},
	faq: {
		eyebrow: "FAQ",
		heading: "Your question is probably here",
		sub: "Didn't find your answer? Leave your number and we'll call you.",
		items: [
			{
				q: "Do I need prior experience?",
				a: "No specific prerequisite. Having a project idea or an existing activity is a plus.",
			},
			{
				q: "How long is the course?",
				a: "18 training hours spread over 3 intensive days.",
			},
			{
				q: "Will I get a certificate?",
				a: "Yes, a recognized certificate of completion plus access to the Moustachir Academy alumni network.",
			},
			{
				q: "How do I find out prices and dates?",
				a: "Leave your details below and our team will walk you through everything: available dates and prices.",
			},
		],
	},
	register: {
		eyebrow: "Registration",
		heading: "Book your seat now",
		sub: "Our team will call you to walk you through the full program, dates and prices, with no commitment.",
		imageAlt: "Dr. Ayoub Boukhatem during a training workshop",
	},
	footer: {
		tagline: "Moustachir Academy, entrepreneurship and business development.",
		book: "Book my seat",
		rights: "© 2026 Moustachir Academy. All rights reserved.",
	},
	whatsapp: {
		aria: "Contact on WhatsApp",
		title: "WhatsApp",
		prefill:
			'Hello, I\'d like to know more about the "Company Creation" course and book a seat.',
	},
	messenger: {
		aria: "Contact us on Messenger",
		title: "Messenger",
	},
	form: {
		fullName: "Full name",
		phone: "Phone number",
		email: "Email",
		wilaya: "Wilaya",
		wilayaPlaceholder: "Your wilaya",
		jobStatus: "Your current situation",
		jobStatusPlaceholder: "Your situation",
		submit: "Book my seat",
		privacy: "Your data is never shared with anyone.",
		successTitle: "Request received",
		successText:
			"Our team will contact you soon to finalize your registration and answer your questions.",
		errName: "Please enter your full name",
		errPhone: "Please enter a valid Algerian phone number",
		errEmail: "Please enter a valid email address",
		errWilaya: "Please choose your wilaya",
		errJob: "Please choose your current situation",
		errGeneric:
			"Something went wrong, please try again or contact us directly.",
		jobStatuses: [
			"Employee",
			"Student",
			"Project owner",
			"Freelancer or craftsman",
			"Job seeker",
			"Other",
		],
	},
	langSwitch: { label: "Language" },
	brochure: {
		metaTitle: "Full fact sheet | Company Creation | Moustachir Academy",
		metaDescription:
			"The complete detailed program of the Company Creation course in Algeria.",
		cardCode: "Training sheet MA-ENT-01",
		eyebrow: "Entrepreneurship",
		title: "Company Creation in Algeria",
		intro:
			"Turning an idea or a project into an organized, competitive company able to grow sustainably in the Algerian economic context.",
		formatLabel: "Format",
		formatValue: "Hands-on group workshops",
		durationLabel: "Duration",
		durationValue: "18 training hours (3 days)",
		audienceLabel: "Target audience",
		audienceValue:
			"Project owners, business founders, entrepreneurs, SME managers, and development officers.",
		prereqHeading: "Prerequisites",
		prereqText:
			"No specific prerequisite. A project idea or an existing activity is a plus.",
		objectivesHeading: "Expected outcomes",
		objectives: [
			"Develop entrepreneurial behavior and identify a documented business opportunity.",
			"Build a viable economic model and prepare a complete, bank-fundable business plan.",
			"Choose the right legal form and master the administrative procedures to set up the company in Algeria.",
			"Define a marketing and commercial strategy and drive the company's sustainable development.",
		],
		programHeading: "Detailed program",
		programSplit: "9 modules · 18 hours over 3 days",
		modules: [
			{
				title: "Module 1: Developing the entrepreneurial mindset",
				text: "Traits of the successful entrepreneur, key success factors, mistakes to avoid, entrepreneurial vision and leadership posture.",
			},
			{
				title: "Module 2: Identifying the opportunity and analyzing the market",
				text: "Identifying the opportunity, market research, competitive analysis, target customers, and value proposition.",
			},
			{
				title: "Module 3: Designing the economic model",
				text: "Business Model Canvas, value creation, revenue streams, cost structure, and validation of the economic model.",
			},
			{
				title: "Module 4: Building the business plan",
				text: "Project presentation, commercial study, marketing plan, organization, financial projections, and financing plan.",
			},
			{
				title: "Module 5: Legal, administrative and tax structuring",
				text: "Legal forms in Algeria, choosing the right status, administrative setup procedures, tax and social obligations, and administrative organization.",
			},
			{
				title: "Module 6: Marketing and commercial development",
				text: "Positioning and commercial policy, prospecting and customer acquisition, loyalty, digital marketing, and growing the client portfolio.",
			},
			{
				title: "Module 7: Organizing and steering the company",
				text: "Internal organization, dashboards and performance indicators (KPIs), risk management, and continuous improvement.",
			},
			{
				title: "Module 8: Leadership and sustainable development",
				text: "Entrepreneurial leadership, communication, motivation, change management, company longevity, and strategic vision.",
			},
			{
				title: "Module 9: Development, growth and sustainability",
				text: "Progressive growth strategies, revenue development, offer diversification, strategic partnerships, innovation, and growth risk management.",
			},
		],
		workshopTitle: "Hands-on workshop",
		workshopText:
			"Collective build of the Business Model Canvas and starting a simplified business plan based on each participant's own project.",
		approachHeading: "Training approach",
		approachText:
			"Our approach favors practice (70%) over theory (30%). Participants work on concrete cases and on their own project (learning by doing) to ensure immediate applicability.",
		evaluationHeading: "Assessment and follow-up",
		evaluation: [
			{
				step: "Before the course",
				text: "A positioning questionnaire to assess the level.",
			},
			{
				step: "During the course",
				text: "Interactive exercises and progress checks on cases from the participant's own project, by the trainer.",
			},
			{
				step: "After the course",
				text: "A certificate of completion and access to the alumni network to ensure immediate applicability.",
			},
		],
		trainerHeading: "The trainer",
		trainerCreds: [
			{ value: "+10,000", label: "trainees worldwide" },
			{ value: "+10", label: "countries trained in" },
		],
		includedHeading: "What's included",
		included: [
			"Recognized certificate of completion",
			"Access to the alumni network",
			"Practical templates and documents (business plan and BMC)",
			"Hands-on workshops on your own project",
			"Follow-up after the course",
		],
		downloadHeading: "Download the full fact sheet (PDF)",
		downloadNote: "The complete printable version, in Arabic and French.",
		downloadAr: "عربية (AR)",
		downloadFr: "Français (FR)",
		ctaHeading: "Ready to build your company?",
		ctaText:
			"Leave your details and our team will get back to you with dates, prices and all the details, with no commitment.",
		ctaButton: "Book my seat",
		backToSite: "Back to home",
	},
};

export const DICT: Record<Locale, Dict> = { ar, fr, en };

// Global site-chrome copy (404, loading, …) — deliberately kept separate from
// `Dict` above, since that type also doubles as the required shape for each
// Training's own `content` JSON (see scripts/seed-*.ts) and this isn't
// per-training content.
export const UI_STRINGS: Record<
	Locale,
	{
		notFound: {
			eyebrow: string;
			title: string;
			description: string;
			cta: string;
		};
		error: {
			eyebrow: string;
			title: string;
			description: string;
			retry: string;
			cta: string;
		};
	}
> = {
	ar: {
		notFound: {
			eyebrow: "خطأ 404",
			title: "الصفحة غير موجودة",
			description: "الرابط الذي فتحته غير متاح، ربما تم نقله أو حذفه.",
			cta: "العودة إلى الصفحة الرئيسية",
		},
		error: {
			eyebrow: "خطأ غير متوقع",
			title: "حدث خطأ ما",
			description:
				"واجه الموقع مشكلة تقنية غير متوقعة. حاول إعادة تحميل الصفحة، وإن استمرت المشكلة تواصل معنا.",
			retry: "إعادة المحاولة",
			cta: "العودة إلى الصفحة الرئيسية",
		},
	},
	fr: {
		notFound: {
			eyebrow: "Erreur 404",
			title: "Page introuvable",
			description:
				"Le lien que vous avez ouvert n'est pas disponible — il a peut-être été déplacé ou supprimé.",
			cta: "Retour à l'accueil",
		},
		error: {
			eyebrow: "Erreur inattendue",
			title: "Une erreur est survenue",
			description:
				"Le site a rencontré un problème technique inattendu. Essayez de recharger la page ; si le problème persiste, contactez-nous.",
			retry: "Réessayer",
			cta: "Retour à l'accueil",
		},
	},
	en: {
		notFound: {
			eyebrow: "404 error",
			title: "Page not found",
			description:
				"The link you opened isn't available — it may have moved or been removed.",
			cta: "Back to home",
		},
		error: {
			eyebrow: "Unexpected error",
			title: "Something went wrong",
			description:
				"The site ran into an unexpected technical issue. Try reloading the page — if it keeps happening, get in touch with us.",
			retry: "Try again",
			cta: "Back to home",
		},
	},
};

/* -------------------------------------------------------------------------- */
/*  Cookie access (isomorphic) + provider                                     */
/* -------------------------------------------------------------------------- */

/** Reads the locale cookie on both the server (request) and client (document). */
export const readLocaleCookie = createIsomorphicFn()
	.server((): Locale => {
		const value = getCookie(LOCALE_COOKIE);
		return isLocale(value) ? value : DEFAULT_LOCALE;
	})
	.client((): Locale => {
		const match = document.cookie.match(
			new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=([^;]+)`),
		);
		const value = match ? decodeURIComponent(match[1]) : undefined;
		return isLocale(value) ? value : DEFAULT_LOCALE;
	});

interface I18nValue {
	locale: Locale;
	dir: "rtl" | "ltr";
	t: Dict;
	setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({
	initialLocale,
	children,
}: {
	initialLocale: Locale;
	children: React.ReactNode;
}) {
	const [locale, setLocaleState] = useState<Locale>(initialLocale);
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	const setLocale = useCallback((next: Locale) => {
		setLocaleState(next);
		// Persist for the next SSR render (1 year). A plain document.cookie write
		// is intentional here — the sync Cookie Store API isn't universally available.
		// biome-ignore lint/suspicious/noDocumentCookie: broad-support cookie write
		document.cookie = `${LOCALE_COOKIE}=${next};path=/;max-age=31536000;samesite=lax`;
	}, []);

	// Keep the document title in sync with the active language. Training routes
	// own their own title sync from their DB content instead (see
	// src/routes/training/$slug.tsx) since their copy isn't in the global DICT.
	useEffect(() => {
		if (pathname.startsWith("/training/")) return;
		document.title = DICT[locale].meta.title;
	}, [locale, pathname]);

	const value = useMemo<I18nValue>(
		() => ({
			locale,
			dir: LOCALE_META[locale].dir,
			t: DICT[locale],
			setLocale,
		}),
		[locale, setLocale],
	);

	return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
	const ctx = useContext(I18nContext);
	if (!ctx) {
		throw new Error("useI18n must be used within an I18nProvider");
	}
	return ctx;
}

/** Convenience: the active dictionary. */
export function useT(): Dict {
	return useI18n().t;
}
